/* MNLY smooth scroll (Lenis) + entrance choreography (Motion).
   Loaded before mnly-home.js so window.MNLYReveal exists by the time the
   reveal observers in that file start firing.

   Two independent halves, each self-gating:
     1. Lenis  — desktop pointer only, wired to whichever element actually
        scrolls (above 990px base.css puts overflow:hidden on html/body and
        makes .page-wrapper the scroller, so window mode would silently no-op).
     2. Motion — takes over the [data-reveal] entrance so items can stagger.
        The CSS transition in mnly-v2.css stays as the fallback for no-JS,
        reduced motion, and any load where this bundle fails. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Entrance choreography ──────────────────────────────────────────────
     mnly-home.js owns the IntersectionObservers (it also handles the
     horizontal-rail grouping). It calls through to MNLYReveal when we're
     here, so there is exactly one trigger and one animator.

     Language: opacity 0→1 with an upward settle. Media travels further and
     lands slower than text so images read as arriving rather than blinking on.

     Two curves, deliberately. Travel keeps expo-out — a hard decelerating
     arrival is the point. The FADE must not: expo-out puts 84% of the opacity
     ramp into the first 25% of the duration, so a nominal 700ms entrance was
     perceptually over in ~175ms and read as a pop no matter how long the
     number said it was. Opacity gets a symmetric curve and finishes just
     before the travel does, so things materialise and then settle.

     Sections that author a --rd delay keep it as art direction; it is scaled
     by RD_SCALE alongside the default step so the authored ratios survive a
     change of base rhythm without editing every section. */
  var Motion = window.Motion;
  if (Motion && Motion.animate && !reduce) {
    document.documentElement.classList.add('mnly-motion');

    var EASE_MOVE = [0.16, 1, 0.3, 1];  /* expo-out: travel decelerates in */
    var EASE_FADE = [0.33, 0, 0.25, 1]; /* eased both ends: no front-loading */
    var BASE = 0.09;      /* a beat before a batch starts, so groups breathe */
    var LOAD_BASE = 0.22; /* first batch after load; harmonises with the
                             350ms mobile hero copy drop in mnly-home.css */
    var STEP = 0.12;      /* per-item stagger, capped at index 6 */
    var RD_SCALE = 1.5;   /* authored --rd retimed with the base rhythm */
    var queue = [];
    var queued = false;
    var firstBatch = true;

    function flush() {
      queued = false;
      var base = firstBatch ? LOAD_BASE : BASE;
      firstBatch = false;
      var groups = new Map();
      queue.forEach(function (el) {
        var key = el.parentElement || document.body;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(el);
      });
      queue.length = 0;

      groups.forEach(function (els) {
        els.forEach(function (el, i) {
          var authored = parseFloat(el.style.getPropertyValue('--rd')) || 0;
          var media = el.__mnlyMedia;
          var opts;

          if (el.__mnlyHero) {
            /* The hero photo is the desktop LCP element, so it keeps its old
               entrance untouched: unscaled authored delay, one expo-out curve
               for both properties. The slower fade below is right everywhere
               the user has already had a moment to look, and wrong here. */
            opts = { duration: 0.9, delay: authored / 1000, ease: EASE_MOVE };
          } else {
            var delay = base + (authored ? (authored / 1000) * RD_SCALE : Math.min(i, 6) * STEP);
            opts = {
              duration: media ? 1.25 : 1.05,
              delay: delay,
              ease: EASE_MOVE,
              /* delay is repeated: a per-property override replaces the shared
                 options rather than merging into them, and without it every
                 fade started at zero and the stagger vanished. */
              opacity: { duration: media ? 1 : 0.85, delay: delay, ease: EASE_FADE }
            };
          }

          Motion.animate(
            el,
            { opacity: [0, 1], transform: [el.style.transform, 'translateY(0px)'] },
            opts
          ).then(function () {
            /* Motion leaves transform:translateY(0px) inline, which outranks
               any CSS :hover transform (card lifts died). It recommits the
               style after this promise resolves, so clear one frame later. */
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                el.style.removeProperty('transform');
                el.style.removeProperty('opacity');
              });
            });
          });
        });
      });
    }

    /* .in stays authoritative: child cascades across mnly-v2.css key off it
       (.tl-form-cat-grid.in span, .mapping-marker.in span, and friends).

       The start state has to be written in the SAME frame as .in. Adding .in
       alone flipped the element to opacity:1/translateY(0) — .mnly-motion
       kills the CSS transition — and Motion only committed its own from-value
       on the next rAF, so every reveal flashed at full strength for two frames
       before fading in from zero. That double-take was most of what read as
       "everything just loads up fast". */
    window.MNLYReveal = function (el) {
      /* A hard-coded .in in the markup means "paint immediately" (hero copy,
         LCP): never animate it. The mobile hero's CSS child choreography
         depends on the parent staying opaque. */
      if (el.classList.contains('in')) return;
      var media = el.tagName === 'IMG' || !!el.querySelector('img, picture, video, svg');
      var hero = el.classList.contains('m-hero-visual');
      el.__mnlyMedia = media;
      el.__mnlyHero = hero;
      el.style.opacity = '0';
      el.style.transform = 'translateY(' + (hero ? 32 : media ? 40 : 24) + 'px)';
      el.classList.add('in');
      queue.push(el);
      if (queued) return;
      queued = true;
      requestAnimationFrame(flush);
    };
  }

  /* ─── Smooth scroll ──────────────────────────────────────────────────────
     Lenis drives real scrollTop on the scroller, never a transform, so
     position:sticky (.join-lead) and position:fixed (the header) are
     untouched. naiveDimensions reads the live scrollHeight each frame, which
     is what we want when the wrapper is itself the scroll container and its
     own box never resizes. */
  var desktop = window.matchMedia('(min-width: 990px)');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  var lenis = null;

  function scroller() {
    var pw = document.querySelector('.page-wrapper');
    if (!pw) return null;
    return getComputedStyle(pw).overflowY === 'auto' || getComputedStyle(pw).overflowY === 'scroll' ? pw : null;
  }

  /* The beta invitation gate holds .page-wrapper at overflow:hidden until
     unlock, then fires resize, so Lenis can boot on window and must rebind.
     Only rebind toward the wrapper: drawers lock it the same way, and a
     resize under an open drawer must not strand Lenis on window. */
  function sync() {
    var want = !reduce && desktop.matches && fine.matches && window.Lenis;
    var pw = want ? scroller() : null;
    if (lenis && (!want || (pw && lenis.options.wrapper !== pw))) {
      lenis.destroy();
      lenis = null;
      window.mnlyLenis = null;
    }
    if (want && !lenis) {
      lenis = new window.Lenis({
        wrapper: pw || window,
        content: pw || document.documentElement,
        naiveDimensions: !!pw,
        lerp: 0.1,
        smoothWheel: true,
        syncTouch: false,
        anchors: true,
        autoRaf: true
      });
      window.mnlyLenis = lenis;
    }
  }

  function boot() {
    sync();
    desktop.addEventListener('change', sync);
    fine.addEventListener('change', sync);
    window.addEventListener('resize', sync);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
